import {
  Clapperboard,
  Gift,
  Globe,
  Percent,
  Sparkles,
  Star,
  UserPlus,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  channelLabel,
  redemptionChannel,
  referralAnalytics,
  rewardChip,
  sharingDisplayName,
  sharingTotals,
  type ReferralChannel,
  type ReferralRelationship,
  type RewardChip,
  type SharingRedemptionRow,
  type SharingReferrerRow,
} from "./sharingContract";
import type { EchoflicksVideoSession } from "../types";

// Presentational: mounted with plain props (no fetch / ra-core context) so
// tests can assert rendered behavior directly — same style as
// GuerillaCodesView.
export function SharingView({
  referrers,
  redemptions,
  echoes,
}: {
  referrers: SharingReferrerRow[];
  redemptions: SharingRedemptionRow[];
  echoes: EchoflicksVideoSession[];
}) {
  const totals = useMemo(
    () => sharingTotals(referrers, redemptions),
    [redemptions, referrers],
  );
  const analytics = useMemo(
    () => referralAnalytics(referrers, redemptions, echoes),
    [echoes, redemptions, referrers],
  );

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Gift codes issued"
          value={totals.codesIssued}
          icon={Gift}
        />
        <MetricCard
          label="Gifts redeemed"
          value={totals.totalRedemptions}
          icon={Sparkles}
          detail="Friends who claimed a code on WhatsApp or the web"
        />
        <MetricCard
          label="New users referred"
          value={totals.newUsers}
          icon={UserPlus}
          detail="New to Felix when they claimed the code"
        />
        <MetricCard
          label="Joined on the web"
          value={totals.webRedemptions}
          icon={Globe}
          detail="Claimed a my.echoflicks.com invite link"
        />
        <MetricCard
          label="Active referrers"
          value={totals.activeReferrers}
          icon={Users}
          detail="Codes with at least one redemption"
        />
        <MetricCard
          label="Friends making Echoes"
          value={analytics.echoMakers}
          icon={Clapperboard}
          detail="Referred friends with a delivered Echo"
        />
        <MetricCard
          label="Referral Echoes"
          value={analytics.referralEchoes}
          icon={Sparkles}
          detail="All delivered Echoes made by referred friends"
        />
        <MetricCard
          label="Echo conversion"
          value={formatPercent(analytics.echoConversionRate)}
          icon={Percent}
          detail="Redeemed friends who made an Echo"
        />
        <MetricCard
          label="Credits earned"
          value={totals.creditsEarned}
          icon={Gift}
          detail="Free Echoes granted to referrers"
        />
        <MetricCard
          label="Credits pending"
          value={totals.creditsPending}
          icon={Clapperboard}
          detail="Redeemed, credit not granted yet"
        />
        <MetricCard
          label="Average referral rating"
          value={
            analytics.averageRating == null
              ? "—"
              : analytics.averageRating.toFixed(1)
          }
          icon={Star}
          detail={`${formatNumber(analytics.ratedEchoes)} rated referral Echo${analytics.ratedEchoes === 1 ? "" : "es"}`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Referrers</CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.referrers.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              No gift codes yet. A code is created the first time a user taps
              “Gift a Free Echo”.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referrer</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="text-right">Redemptions</TableHead>
                    <TableHead className="text-right">New users</TableHead>
                    <TableHead className="text-right">
                      Friends with Echoes
                    </TableHead>
                    <TableHead className="text-right">
                      Referral Echoes
                    </TableHead>
                    <TableHead className="text-right">Conversion</TableHead>
                    <TableHead className="text-right">Credits</TableHead>
                    <TableHead>Last redemption</TableHead>
                    <TableHead>Code created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.referrers.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <ContactCell
                          contactId={row.owner_contact_id}
                          name={row.owner_name}
                          waId={row.owner_wa_id}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.code}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.redemptions)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.new_users)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.echoMakers)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.referralEchoes)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPercent(row.echoConversionRate)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(Number(row.credits_earned) || 0)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(row.last_redeemed_at) ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(row.created_at) ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Who invited whom</CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.relationships.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              No redemptions yet. They appear here the moment a friend claims a
              gift code on WhatsApp or the web.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Referrer</TableHead>
                    <TableHead>Friend</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Relationship</TableHead>
                    <TableHead>Credit</TableHead>
                    <TableHead className="text-right">Echoes</TableHead>
                    <TableHead>Latest Echo</TableHead>
                    <TableHead className="text-right">Rating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.relationships.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(row.redeemed_at) ?? "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.code}
                      </TableCell>
                      <TableCell>
                        <ContactCell
                          contactId={row.referrer_contact_id}
                          name={row.referrer_name}
                          waId={row.referrer_wa_id}
                        />
                      </TableCell>
                      <TableCell>
                        <ContactCell
                          contactId={row.referred_contact_id}
                          name={row.referred_name}
                          waId={row.referred_wa_id}
                        />
                      </TableCell>
                      <TableCell>
                        <ChannelBadge channel={redemptionChannel(row)} />
                      </TableCell>
                      <TableCell>
                        {row.referred_is_new ? (
                          <Badge>New user</Badge>
                        ) : (
                          <Badge variant="secondary">Existing user</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <RewardBadge chip={rewardChip(row)} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.echoes.length)}
                      </TableCell>
                      <TableCell>
                        <EchoCell relationship={row} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatRating(row.latestEcho?.rating)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ChannelBadge({ channel }: { channel: ReferralChannel }) {
  return (
    <Badge variant={channel === "web" ? "secondary" : "outline"}>
      {channelLabel(channel)}
    </Badge>
  );
}

function RewardBadge({ chip }: { chip: RewardChip }) {
  if (chip.tone === "earned") {
    return <Badge>{chip.label}</Badge>;
  }
  if (chip.tone === "muted") {
    return <span className="text-muted-foreground">{chip.label}</span>;
  }
  return (
    <Badge variant={chip.tone === "skipped" ? "outline" : "secondary"}>
      {chip.label}
    </Badge>
  );
}

function EchoCell({ relationship }: { relationship: ReferralRelationship }) {
  const echo = relationship.latestEcho;
  if (!echo) return <span className="text-muted-foreground">—</span>;
  const label = echo.movie_title ?? echo.script_title ?? "View Echo";
  return (
    <div className="min-w-40">
      <Link
        className="text-primary hover:underline"
        to={`/echoflicks_echoes/${echo.id}/show`}
      >
        {label}
      </Link>
      <p className="text-xs text-muted-foreground">
        {formatDate(echo.completed_at ?? echo.display_date) ?? "Delivered"}
      </p>
    </div>
  );
}

// Contact-resolved cell: link into the CRM contact when the SQL phone join
// matched, plain wa_id text otherwise (same fallback as EchoList).
function ContactCell({
  contactId,
  name,
  waId,
}: {
  contactId: number | string | null;
  name: string | null;
  waId: string;
}) {
  const label = sharingDisplayName(name, waId);
  if (contactId == null) {
    return <span>{label}</span>;
  }
  return (
    <Link
      className="text-primary hover:underline"
      to={`/contacts/${contactId}/show`}
    >
      {label}
    </Link>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  detail,
}: {
  label: string;
  value: number | string;
  icon: typeof Gift;
  detail?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-3 p-5">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {typeof value === "number" ? formatNumber(value) : value}
          </p>
          {detail ? (
            <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
          ) : null}
        </div>
        <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
      </CardContent>
    </Card>
  );
}

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(Number(value) || 0);
}

function formatPercent(value: number) {
  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  }).format(100 * value)}%`;
}

function formatRating(value?: number | null) {
  const rating = Number(value);
  return Number.isFinite(rating) && rating >= 1 && rating <= 5
    ? rating.toFixed(1)
    : "—";
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
